'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import {
  UNSAVED_CHANGES_BYPASS_EVENT,
  useUnsavedChangesPrompt,
} from '@/features/tool/hooks/useUnsavedChangesPrompt';
import { isBrowserBackCloseTraversal } from '@/hooks/useCloseOnBrowserBack';
import { store } from '@/store';

const HISTORY_GUARD_KEY = '__unsaved_changes_guard__';
const SUPPRESSED_CONFIRM_MS = 50;
const PENDING_TRAVERSAL_TIMEOUT_MS = 1000;

const getTargetPath = (url: URL) => `${url.pathname}${url.search}${url.hash}`;

const isModifiedClick = (event: MouseEvent) =>
  event.button !== 0 ||
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey;

const UnsavedChangesGuard = () => {
  const router = useRouter();
  const {
    hasUnsavedChanges,
    confirmDiscard,
    discardChanges,
    discardChangesAndRun,
  } =
    useUnsavedChangesPrompt();
  const hasUnsavedChangesRef = React.useRef(hasUnsavedChanges);
  const bypassRef = React.useRef(false);
  const pendingTraversalRef = React.useRef(false);

  React.useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleBypassEvent = (
      event: Event
    ) => {
      const enabled = (event as CustomEvent<{ enabled?: boolean }>).detail
        ?.enabled;

      bypassRef.current = Boolean(enabled);
    };

    window.addEventListener(
      UNSAVED_CHANGES_BYPASS_EVENT,
      handleBypassEvent as EventListener
    );

    return () => {
      window.removeEventListener(
        UNSAVED_CHANGES_BYPASS_EVENT,
        handleBypassEvent as EventListener
      );
    };
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChangesRef.current || bypassRef.current) {
        return;
      }

      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !hasUnsavedChanges) {
      return;
    }

    const guardedHref = window.location.href;

    const isOnGuardEntry = () =>
      Boolean(window.history.state?.[HISTORY_GUARD_KEY]);

    const traverse = (delta: number) => {
      bypassRef.current = true;
      pendingTraversalRef.current = true;
      window.history.go(delta);
      window.setTimeout(() => {
        bypassRef.current = false;
        pendingTraversalRef.current = false;
      }, PENDING_TRAVERSAL_TIMEOUT_MS);
    };

    const reassertGuardEntry = () => {
      if (isOnGuardEntry()) {
        window.history.replaceState(window.history.state, '');
      }
    };

    const handlePopState = (event: PopStateEvent) => {
      if (pendingTraversalRef.current) {
        pendingTraversalRef.current = false;
        return;
      }

      if (
        !hasUnsavedChangesRef.current ||
        bypassRef.current ||
        isBrowserBackCloseTraversal(event.state)
      ) {
        return;
      }

      const confirmStartedAt = performance.now();
      const confirmed = confirmDiscard();
      const wasSuppressed =
        !confirmed &&
        performance.now() - confirmStartedAt < SUPPRESSED_CONFIRM_MS;

      if (!confirmed && !wasSuppressed) {
        traverse(1);
        return;
      }

      discardChanges();
      if (window.location.href === guardedHref) {
        traverse(-1);
      }
    };

    let isPushed = false;

    const pushGuardEntry = () => {
      isPushed = true;
      window.history.pushState(
        {
          ...(window.history.state || {}),
          [HISTORY_GUARD_KEY]: true,
        },
        '',
        window.location.href
      );
      window.addEventListener('popstate', handlePopState);
      document.addEventListener('pointerdown', reassertGuardEntry, {
        capture: true,
        once: true,
      });
    };

    const pushGuardEntryWhenVisible = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      document.removeEventListener(
        'visibilitychange',
        pushGuardEntryWhenVisible
      );
      pushGuardEntry();
    };

    if (document.visibilityState === 'hidden') {
      document.addEventListener('visibilitychange', pushGuardEntryWhenVisible);
    } else {
      pushGuardEntry();
    }

    return () => {
      document.removeEventListener(
        'visibilitychange',
        pushGuardEntryWhenVisible
      );
      if (isPushed) {
        window.removeEventListener('popstate', handlePopState);
        document.removeEventListener('pointerdown', reassertGuardEntry, true);
      }
    };
  }, [confirmDiscard, discardChanges, hasUnsavedChanges]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(window.history);

    const interceptHistoryChange =
      (method: 'push' | 'replace') =>
      (state: any, title: string, url?: string | URL | null) => {
        const hasUnsavedChangesNow = store.getState().unsavedChanges.isDirty;
        if (!url || bypassRef.current || !hasUnsavedChangesNow) {
          return method === 'push'
            ? originalPushState(state, title, url)
            : originalReplaceState(state, title, url);
        }

        const nextUrl = new URL(url.toString(), window.location.href);
        const currentUrl = new URL(window.location.href);

        if (getTargetPath(nextUrl) === getTargetPath(currentUrl)) {
          return method === 'push'
            ? originalPushState(state, title, url)
            : originalReplaceState(state, title, url);
        }

        discardChangesAndRun(() => {
          bypassRef.current = true;
          const targetPath = getTargetPath(nextUrl);

          if (method === 'replace') {
            router.replace(targetPath);
          } else {
            router.push(targetPath);
          }

          window.setTimeout(() => {
            bypassRef.current = false;
          }, 0);
        });

        return;
      };

    window.history.pushState = interceptHistoryChange('push');
    window.history.replaceState = interceptHistoryChange('replace');

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [discardChangesAndRun, router]);

  React.useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (
        bypassRef.current ||
        !hasUnsavedChangesRef.current ||
        event.defaultPrevented ||
        isModifiedClick(event)
      ) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;

      if (!anchor) {
        return;
      }

      if (anchor.target === '_blank' || anchor.hasAttribute('download')) {
        return;
      }

      const nextUrl = new URL(anchor.href, window.location.href);
      const currentUrl = new URL(window.location.href);

      if (
        nextUrl.origin !== currentUrl.origin ||
        getTargetPath(nextUrl) === getTargetPath(currentUrl)
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      discardChangesAndRun(() => {
        bypassRef.current = true;
        router.push(getTargetPath(nextUrl));
        window.setTimeout(() => {
          bypassRef.current = false;
        }, 0);
      });
    };

    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [discardChangesAndRun, router]);

  return null;
};

export default UnsavedChangesGuard;
